// Sprint PKG-VIZ A1 (#792) — pkg_edge_type → visual style.
//
// Recon (Sprint-Artefacts/Sprint-A1-recon.md §1.6) found the issue's
// edge-styling table named edge types that don't exist in the real
// pkg_edge_type enum. Adjudication (Q4) locked this mapping instead,
// keyed on the real 6-value enum. Colors are provisional-but-binding —
// cosmetic, not part of the @blueprint/shared type contract, so a later
// color change here is a one-file fix, not a breaking change for
// downstream consumers.

import { MarkerType, type EdgeMarkerType } from '@xyflow/react';
import type { PKGEdgeType } from '@blueprint/shared';
import { colors } from '../../../config/design-tokens.ts';

const AI_INFERRED_DASH = '5,5';

export interface PKGEdgeVisualStyle {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
  markerEnd?: EdgeMarkerType;
}

const BASE_EDGE_STYLES: Record<PKGEdgeType, PKGEdgeVisualStyle> = {
  // evidence_link — solid graphite line (issue's one accurate edge claim).
  EVIDENCE_LINK: {
    stroke: colors.graphite[600],
    strokeWidth: 1.5,
  },
  // Decision tied to an entity — violet arrow.
  DECISION_OF: {
    stroke: colors.primary.DEFAULT,
    strokeWidth: 1.5,
    markerEnd: { type: MarkerType.ArrowClosed, color: colors.primary.DEFAULT },
  },
  // Risk associated with an entity — pink line.
  RISK_OF: {
    stroke: colors.accent.DEFAULT,
    strokeWidth: 1.5,
  },
  // Story implements a requirement — violet dashed.
  IMPLEMENTS: {
    stroke: colors.primary.DEFAULT,
    strokeWidth: 1.5,
    strokeDasharray: '4,3',
  },
  // Artefact derived from another — graphite dashed.
  DERIVED_FROM: {
    stroke: colors.graphite[600],
    strokeWidth: 1.5,
    strokeDasharray: '4,3',
  },
  // Generic catch-all reference — default/gray solid.
  REFERENCES: {
    stroke: colors.semantic.muted,
    strokeWidth: 1,
  },
};

// R2 fix (F2): the node path has a fallback (FallbackNode, S1) for any
// entity_type outside the 6 named ones — edges had no equivalent, so an
// edgeType outside the 6 real values crashed toFlowEdges reading
// .stroke off undefined. This is the edge-side mirror of that fallback:
// same muted/thin treatment FallbackNode uses, distinguishable from
// REFERENCES only in that it signals "unrecognized," not "intentionally
// generic."
const FALLBACK_EDGE_STYLE: PKGEdgeVisualStyle = {
  stroke: colors.semantic.muted,
  strokeWidth: 1,
  strokeDasharray: '2,2',
};

function baseStyleFor(edgeType: PKGEdgeType): PKGEdgeVisualStyle {
  // Own-property check, not bracket access alone — same prototype-chain
  // hazard as F1 applies here (BASE_EDGE_STYLES is a plain object).
  return Object.prototype.hasOwnProperty.call(BASE_EDGE_STYLES, edgeType)
    ? BASE_EDGE_STYLES[edgeType]
    : FALLBACK_EDGE_STYLE;
}

/**
 * Resolves the visual style for an edge. `createdByAi` only overrides the
 * dash pattern (forces dashed) — the type's base color/weight are always
 * preserved, so an AI-inferred RISK_OF edge is still recognizably pink,
 * just dashed instead of solid. See adjudication V4. Falls back to
 * FALLBACK_EDGE_STYLE for any edgeType outside the real 6-value enum
 * (F2) — created_by_ai dashing still applies over the fallback.
 */
export function edgeStyleFor(edgeType: PKGEdgeType, createdByAi: boolean): PKGEdgeVisualStyle {
  const base = baseStyleFor(edgeType);
  if (!createdByAi) return base;
  return { ...base, strokeDasharray: AI_INFERRED_DASH };
}

/**
 * Stable, collision-free edge id. Distinct edge types between the same
 * node pair must not collide — pkg_edges only DB-enforces uniqueness on
 * (from, to, edge_type), so (from, to) alone is not a valid id. See
 * adjudication Q10.
 *
 * R2 fix (F6): a plain `${from}-${to}-${type}` join is ambiguous when
 * an id itself contains a hyphen — buildEdgeId('a','b-c','REFERENCES')
 * and buildEdgeId('a-b','c','REFERENCES') both produced 'a-b-c-REFERENCES'.
 * Production pkg_nodes.id values are fixed-format UUIDs so this couldn't
 * collide there, but the contract doesn't guarantee callers only ever
 * use UUID-shaped ids (this diff's own test fixtures don't). Length-
 * prefixing each component makes the encoding unambiguous regardless of
 * what characters the ids contain — no two distinct (from, to, type)
 * triples can ever produce the same string.
 */
export function buildEdgeId(fromNodeId: string, toNodeId: string, edgeType: PKGEdgeType): string {
  return `${fromNodeId.length}:${fromNodeId}|${toNodeId.length}:${toNodeId}|${edgeType}`;
}
